import React from 'react'
import { Typography, theme } from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { ModalFuncProps } from 'antd/es/modal/interface'
import './onec-dialog.css'

type DialogOperation = {
  status?: string
  error_message?: string | null
  preview?: {
    base_name?: string
    number?: string
    date?: string
    document_date?: string
    orders?: string[]
    total?: number | string
    lines?: number
    receipt_number?: string
    object_id?: string
    comment?: string
  }
  remote_result?: { receipt_json?: { objectId?: string } }
  receipt?: { number: string; posted: boolean; object_id: string }
  receipt_warning?: string
}
type DialogProps = {
  operation: DialogOperation
  posting: boolean
  result?: boolean
  pending?: boolean
  error?: string
}
const displayDate = (value?: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value || '')
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value || 'Дата не указана'
}
const displayTotal = (value?: number | string) =>
  value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))
    ? Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—'

export function OnecDialogBody({
  operation,
  posting,
  result = false,
  pending = false,
  error,
}: DialogProps) {
  const { token } = theme.useToken()
  const preview = operation.preview || {}
  const succeeded = ['completed', 'already_posted'].includes(operation.status || '')
  const failed = !!error || (result && operation.status === 'failed')
  const tone = failed ? 'error' : result && succeeded ? 'success' : 'info'
  const Icon = failed
    ? WarningOutlined
    : result && succeeded
    ? CheckCircleOutlined
    : pending || result
    ? ClockCircleOutlined
    : FileTextOutlined
  const title = failed
    ? error
      ? 'Не удалось завершить действие'
      : posting
      ? 'Проведение не выполнено'
      : 'Отправка не выполнена'
    : pending
    ? posting
      ? 'Проводим документ в 1С'
      : 'Создаём приходную в 1С'
    : result
    ? succeeded
      ? posting
        ? 'Проведение подтверждено'
        : 'Приходная создана без проведения'
      : 'Результат пока не подтверждён'
    : posting
    ? 'Проведение существующей приходной'
    : 'Создание без проведения'
  const description = failed
    ? error || operation.error_message || 'Проверьте результат операции перед повторной попыткой.'
    : pending
    ? 'Дождитесь ответа 1С. Результат появится в карточке накладной.'
    : result
    ? succeeded
      ? undefined
      : operation.error_message || 'Проверьте результат текущей операции в карточке накладной.'
    : posting
    ? '1С выполнит проверки и сформирует движения по документу.'
    : 'Приходная будет создана на основании выбранных заказов. Проведение доступно отдельным действием.'
  const objectId = operation.receipt?.object_id || operation.remote_result?.receipt_json?.objectId || preview.object_id
  const receiptNumber = operation.receipt?.number || preview.receipt_number
  const variables = {
    '--onec-text': token.colorText,
    '--onec-muted': token.colorTextSecondary,
    '--onec-fill': token.colorFillAlter,
    '--onec-surface': token.colorBgContainer,
    '--onec-primary': token.colorPrimary,
    '--onec-info-bg': token.colorPrimaryBg,
    '--onec-success': token.colorSuccessText,
    '--onec-success-bg': token.colorSuccessBg,
    '--onec-error': token.colorErrorText,
    '--onec-error-bg': token.colorErrorBg,
  } as React.CSSProperties
  return (
    <div className="supply-onec-dialog__body" style={variables}>
      <div className="supply-onec-dialog__invoice">
        <div className="supply-onec-dialog__identity">
          <span className="supply-onec-dialog__label">Входящая накладная</span>
          <strong className="supply-onec-dialog__number">
            {preview.number || 'Номер не указан'}
          </strong>
          <span className="supply-onec-dialog__date">от {displayDate(preview.date)}</span>
        </div>
        <div className="supply-onec-dialog__amount">
          <span className="supply-onec-dialog__label">Сумма с НДС</span>
          <strong>{displayTotal(preview.total)}</strong>
        </div>
      </div>
      <dl className="supply-onec-dialog__requisites">
        <dt>База 1С</dt>
        <dd>{preview.base_name || '—'}</dd>
        {preview.document_date && (
          <>
            <dt>Дата документа в 1С</dt>
            <dd>
              {displayDate(preview.document_date)} {preview.document_date.slice(11, 19)}
            </dd>
          </>
        )}
        {receiptNumber && (
          <>
            <dt>Номер в 1С</dt>
            <dd className="supply-onec-dialog__receipt-number"><a href={`/warehouse/${encodeURIComponent(receiptNumber)}`} target="_blank" rel="noopener noreferrer">{receiptNumber}</a>{operation.receipt && <small> · {operation.receipt.posted ? 'Проведена' : 'Без проведения'}</small>}</dd>
          </>
        )}
        <dt>{(preview.orders?.length || 0) > 1 ? 'Заказы поставщикам' : 'Заказ поставщику'}</dt>
        <dd>
          {preview.orders?.length ? (
            <span className="supply-onec-dialog__orders">
              {preview.orders.map(order => (
                <span key={order}>{order}</span>
              ))}
            </span>
          ) : (
            '—'
          )}
        </dd>
        <dt>Строк в документе</dt>
        <dd>{preview.lines ?? '—'}</dd>
      </dl>
      <div
        className={`supply-onec-dialog__notice supply-onec-dialog__notice--${tone}`}
        role={failed ? 'alert' : 'status'}
        aria-live="polite"
      >
        <Icon />
        <div>
          <strong>{title}</strong>
          {description && <p>{description}</p>}
        </div>
      </div>
      {result && operation.receipt_warning && <p role="alert">{operation.receipt_warning}. Проверьте существующую операцию перед повторной отправкой.</p>}
      {result && (objectId || preview.comment) && (
        <details className="supply-onec-dialog__details">
          <summary>
            <RightOutlined />
            <span>Данные для поиска в 1С</span>
          </summary>
          <div className="supply-onec-dialog__technical">
            {objectId && (
              <div>
                <span className="supply-onec-dialog__label">UUID документа</span>
                <Typography.Paragraph
                  className="supply-onec-dialog__uuid"
                  copyable={{ text: objectId, tooltips: ['Скопировать UUID', 'Скопировано'] }}
                >
                  {objectId}
                </Typography.Paragraph>
              </div>
            )}
            {preview.comment && (
              <div>
                <span className="supply-onec-dialog__label">Комментарий в 1С</span>
                <Typography.Paragraph
                  copyable={{
                    text: preview.comment,
                    tooltips: ['Скопировать комментарий', 'Скопировано'],
                  }}
                >
                  {preview.comment}
                </Typography.Paragraph>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  )
}

export const onecDialogPresentation = (props: DialogProps): ModalFuncProps => ({
  className: 'supply-onec-dialog',
  width: 640,
  centered: true,
  icon: null,
  maskClosable: !!props.result,
  title: props.result
    ? props.posting
      ? 'Результат проведения в 1С'
      : 'Результат отправки в 1С'
    : props.posting
    ? 'Провести приходную накладную в 1С?'
    : 'Отправить накладную в 1С?',
  content: <OnecDialogBody {...props} />,
  okButtonProps: { size: 'large' },
  cancelButtonProps: { size: 'large', disabled: !!props.pending },
  keyboard: !props.pending,
})
